import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronDown, ImageOff, Upload } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
// Canonical public-facing origin for every /[slug] landing link (QR codes,
// Live Preview, "Open landing" buttons in the Client Manager, etc.).
// Comes from `REACT_APP_PUBLIC_URL` in `frontend/.env` so QR codes / share
// links always resolve to the customer-facing custom domain
// (e.g. `https://play.amblemead.biz`) regardless of which admin URL the
// super-admin is signed into. Falls back to the current window origin
// when unset (dev / preview convenience).
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/+$/, "");

/** Absolute public URL for a pub's branded /c/[slug] landing. */
export function publicLandingUrl(slug) {
  if (!slug) return null;
  const base = PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/c/${slug}`;
}

export const ORG_TYPES = [
  { value: "pub_group", label: "Pub Group" },
  { value: "independent_pub", label: "Independent Pub" },
  { value: "football_foundation", label: "Football Club Foundation" },
  { value: "charity", label: "Charity" },
  // "Other" is a single-level catch-all for tenants that don't fit the
  // three canonical org shapes (e.g. community groups, bars, restaurants,
  // members' clubs). Same behaviour as Independent Pub — creates one
  // landing pub row, editable branding, no child pubs.
  { value: "other", label: "Other" },
];
export const ORG_TYPE_LABEL = Object.fromEntries(
  ORG_TYPES.map((o) => [o.value, o.label]),
);

export function absoluteLogoUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${API_BASE}${u}`;
}

export function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    // iter151 — strip possessive/curly apostrophes BEFORE the alphanum
    // replace so "Dugsy's Salford" → "dugsy-salford" (not the confusing
    // "dugsys-salford" that iter150 shipped with).
    .replace(/[’'`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

// Lighter variant used while the user is actively typing in the Slug field —
// preserves a trailing hyphen so mid-word edits (e.g. "founders-" then adding
// more chars) don't get stripped as they type.
export function slugifyLive(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[’'`]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "");
}

/* ---------------------------------------------------------------------------
 * Reusable atoms
 * ------------------------------------------------------------------------- */

export function Field({ label, hint, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
      {hint && <p className="mt-1.5 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );
}

/**
 * SectionCard — the standard section wrapper on the Client Manager.
 *
 * `collapsible` (iter75) turns the header into a click-to-toggle banner:
 * clicking it hides/reveals the section body (children + description). The
 * chevron rotates to indicate state. When `defaultCollapsed` is true the
 * section starts closed — useful for dense pages where the admin only
 * needs to open one section at a time.
 *
 * `dirty` (iter76) — when true, an amber pulse-dot appears to the left of
 * the section title. Signals to the admin that this collapsed section
 * still has edits pending a save. Cleared by the editor when it commits
 * the change (see `useDirtyTracker` hook below).
 *
 * `logoUrl` / `subtitle` / `accentColor` / `actions` (iter78) — turn the
 * banner into a full client-identity card matching the public /[slug]
 * landing:
 *   • `logoUrl` renders a small square logo (falls back to a coloured
 *     initial when absent).
 *   • `subtitle` renders an uppercase caption under the title (usually
 *     `<town> · /<slug>`).
 *   • `accentColor` colours the initial-fallback avatar.
 *   • `actions` is a slot for buttons (Open / Manage / Delete etc.) that
 *     render to the RIGHT of the title but OUTSIDE the toggle button, so
 *     their clicks don't accidentally collapse the section.
 * Together they let admins recognise which pub they're editing without
 * expanding the details form.
 *
 * Backwards compatible: existing callers that don't pass `collapsible`
 * or `dirty` render the exact same layout as before.
 */
export function SectionCard({
  title,
  description,
  children,
  testId,
  collapsible = false,
  defaultCollapsed = false,
  dirty = false,
  logoUrl = null,
  subtitle = null,
  accentColor = null,
  actions = null,
  controlled = false,
  open: controlledOpen,
  onToggle,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!defaultCollapsed);
  const open = controlled ? !!controlledOpen : uncontrolledOpen;
  const toggle = () => {
    if (controlled) onToggle?.(!open);
    else setUncontrolledOpen((v) => !v);
  };
  const isCollapsed = collapsible && !open;

  const initial = (typeof title === "string" ? title : "?").trim().charAt(0).toUpperCase();
  const showIdentityAvatar = !!logoUrl || !!subtitle || !!accentColor;

  const identityBlock = (
    <>
      {showIdentityAvatar && (
        <span
          className="w-10 h-10 shrink-0 rounded-sm flex items-center justify-center overflow-hidden border border-zinc-100"
          style={{ backgroundColor: logoUrl ? "#fff" : (accentColor || "#18181B") }}
          aria-hidden="true"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="max-w-full max-h-full object-contain"
              data-testid={testId ? `${testId}-avatar-logo` : undefined}
            />
          ) : (
            <span className="text-white text-sm font-bold">{initial}</span>
          )}
        </span>
      )}
      <div className="flex-1 min-w-0 text-left">
        <h3 className="font-heading text-lg font-semibold text-zinc-950 inline-flex items-center gap-2">
          {dirty && (
            <span
              data-testid={testId ? `${testId}-dirty-dot` : undefined}
              title="Unsaved changes"
              className="inline-block w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.2)]"
              aria-label="Unsaved changes"
            />
          )}
          {title}
        </h3>
        {subtitle && (
          <p
            className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mt-0.5 truncate"
            data-testid={testId ? `${testId}-subtitle` : undefined}
          >
            {subtitle}
          </p>
        )}
        {description && !isCollapsed && !subtitle && (
          <p className="text-xs text-zinc-500 mt-1">{description}</p>
        )}
      </div>
    </>
  );

  return (
    <section
      data-testid={testId}
      className="bg-white rounded-md border border-zinc-200 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
    >
      {collapsible ? (
        <div
          className={`w-full flex items-center gap-3 px-6 py-4 hover:bg-zinc-50 transition-colors ${
            open ? "border-b border-zinc-100" : ""
          }`}
        >
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            data-testid={testId ? `${testId}-toggle` : undefined}
            className="flex-1 flex items-center gap-3 min-w-0 text-left"
          >
            {identityBlock}
          </button>
          {actions && (
            <div
              className="flex items-center gap-2 shrink-0"
              data-testid={testId ? `${testId}-actions` : undefined}
              // Prevent action-button clicks from bubbling to the toggle.
              onClick={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-hidden="true"
            tabIndex={-1}
            className="shrink-0"
          >
            <ChevronDown
              className={`h-5 w-5 text-zinc-400 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      ) : (
        <header className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          {identityBlock}
          {actions && (
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          )}
        </header>
      )}
      {(!collapsible || open) && (
        <div className="p-6 space-y-5">{children}</div>
      )}
    </section>
  );
}

/**
 * useDirtyTracker — reusable "form has unsaved edits" hook.
 *
 * Takes a JSON-serialisable snapshot of the current form values on each
 * render and compares it to the last committed snapshot. Returns:
 *   - `isDirty` — true when current !== last committed.
 *   - `commit(values?)` — reset the baseline to `values` (default: current)
 *     after a successful save so the dot disappears.
 *
 * Kept intentionally simple — deep-equal via `JSON.stringify` is plenty
 * fast for these small editor forms.
 */
export function useDirtyTracker(values) {
  const initialRef = useRef(JSON.stringify(values));
  const currentJson = JSON.stringify(values);
  const isDirty = currentJson !== initialRef.current;
  const commit = (next) => {
    initialRef.current = JSON.stringify(next === undefined ? values : next);
  };
  return { isDirty, commit };
}

/* Logo uploader — square preview, live-updates via `onUpload`.
 *
 * `readOnly` (iter69) → collapses the component to just the square
 * preview (no Upload / Replace / Remove buttons, no file input). Used
 * by the Client Manager where the Super-Admin is a viewer only —
 * organisation editors upload their own logo from `/c/[slug]/editor`
 * and the Client Manager should surface whatever they saved without
 * offering conflicting edit controls. */
export function LogoField({ current, onUpload, endpointPost, endpointDelete, testId, readOnly = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = absoluteLogoUrl(current);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpointPost, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpload?.(data);
      toast.success("Logo uploaded (auto-resized to 200×200).");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const doRemove = async () => {
    if (!endpointDelete) return;
    setBusy(true);
    try {
      await api.delete(endpointDelete);
      onUpload?.({ logo_url: null });
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div
        className="rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden shrink-0"
        style={{ width: 96, height: 96 }}
      >
        {src ? (
          <img src={src} alt="logo" className="max-w-full max-h-full object-contain" data-testid={`${testId}-preview`} />
        ) : (
          <ImageOff className="h-8 w-8 text-zinc-300" />
        )}
      </div>
      {readOnly ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Read-only preview</p>
          <p className="text-[11px] text-zinc-500 max-w-xs" data-testid={`${testId}-readonly-note`}>
            Managed by the organisation editor from their own dashboard. Refresh to pull the latest.
          </p>
        </div>
      ) : (
      <div className="flex flex-col gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-upload-btn`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8 disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" /> {busy ? "Uploading…" : src ? "Replace logo" : "Upload logo"}
        </button>
        {src && endpointDelete && (
          <button
            type="button"
            onClick={doRemove}
            disabled={busy}
            className="text-[11px] text-zinc-500 hover:text-red-600 underline underline-offset-2 text-left"
            data-testid={`${testId}-remove-btn`}
          >
            Remove
          </button>
        )}
        <p className="text-[10px] text-zinc-400 mt-0.5">Auto-resized to 200×200 · PNG/JPG/WEBP · max 2MB</p>
      </div>
      )}
    </div>
  );
}
